import { Component, computed, effect, inject, output, signal, Signal, viewChild } from "@angular/core";
import { SharedModule } from "../../../shared/shared.module";
import { DxFormModule } from "devextreme-angular";
import { _AbcButtonComponent, _AbcFormComponent, _AbcGridComponent, _AbcSelectBoxComponent } from "@barthauer/abc/ui-components";
import { DataService } from "../../../services/data.service";
import { AddressPerson, NotificationMessage, ToastType } from "@barthauer/custom/xmt-models";
import { StaffMembersService } from "../staff-members.service";
import { IRole } from "../role.interface";
import { toSignal } from "@angular/core/rxjs-interop";
import { CompanyRoleService } from "./company-role.service";
import { ValueChangedEvent } from "devextreme/ui/text_box";
import { Column, ColumnButtonClickEvent, RowPreparedEvent, RowRemovingEvent, RowUpdatingEvent } from "devextreme/ui/data_grid";
import { NotificationService } from "../../../services/notification.service";
import { ValidationCallbackData } from "devextreme/common";
import { first } from "rxjs";
import { PersonStatus } from "../../../models/enumeration-list.model";
@Component({
  selector: "ubt-geods-company-role",
  templateUrl: "./company-role.component.html",
  styleUrls: ["./company-role.component.scss"],
  imports: [
    SharedModule,
    DxFormModule,
    _AbcFormComponent,
    _AbcSelectBoxComponent,
    _AbcButtonComponent,
    _AbcGridComponent
  ],
  providers:[
    CompanyRoleService
  ]
})
export class CompanyRoleComponent{
  protected popupClosed= output<void>();
  
  private readonly dataService = inject(DataService);
  private readonly staffMembersService = inject(StaffMembersService);
  private readonly companyRoleService = inject(CompanyRoleService);
  private readonly notificationService = inject(NotificationService);

  private companyRoleForm=viewChild<_AbcFormComponent>("CompanyRoleForm");
  
  protected companies = toSignal(this.companyRoleService.getCompaniesData(),{initialValue: []});
  protected roles:Signal<IRole[] | null>= computed(()=>this.staffMembersService.positions());
  protected res: (key: string, ...value: string[]) => string = this.dataService.res;
  protected company: Partial<AddressPerson>=new AddressPerson(null);
  protected addressPerson = signal<Partial<AddressPerson>[]>([]);
  protected isAssociateValid = computed(()=>this.newCompanies().length>0 || this.updatedCompanies().length>0 || this.deletedCompanies().length>0);
  protected isLoading=signal(false);
  private isEditing=signal(false);
  protected columnsHeader: Signal<Column[]> = computed(()=>
    [
      {
        caption: this.dataService.res("Ubt-CreateStaffMembers-Company"),
        dataField: "Name",
        allowEditing: false,
      },
      {
        caption: this.dataService.res("Ubt-CreateStaffMembers-Role"),
        dataField: "Position",
        allowEditing: true,
        lookup: {
          dataSource: this.roles(),
          valueExpr: 'Value',
          displayExpr: 'Description'
        }
      },
      {
        type: 'buttons',
        ...(this.isEditing()? {width: 100}:{}),
        buttons: [
          {
            name: 'edit',
            hint: 'Edit',
            visible: (e: ColumnButtonClickEvent) => e.row.data.Status !== PersonStatus.ARCHIVED
          },
          {
            name: 'delete',
            hint: 'Delete',
            visible: (e: ColumnButtonClickEvent) => e.row.data.Status !== PersonStatus.ARCHIVED
          },
          {
            name: 'restore',
            hint: 'Restore',
            icon: 'revert',
            visible: (e: ColumnButtonClickEvent) => e.row.data.Status === PersonStatus.ARCHIVED,
            onClick: (e: ColumnButtonClickEvent) => this.restoreAddressPerson(e)
          }
        ]
      }
    ]
  )

  private selectedPersonId= computed(()=>this.staffMembersService.selectedPersonId());
  private newCompanies = signal<Partial<AddressPerson>[]>([]);
  private updatedCompanies = signal<Partial<AddressPerson>[]>([]);
  private deletedCompanies = signal<Partial<AddressPerson>[]>([]);
  
  constructor(){
    //track when person Id changed
    effect(() => {
      const id = this.selectedPersonId();
      if (id) {
        this.isLoading.set(true);
        this.companyRoleService.getCompaniesByPersonId(id).subscribe(companies => {
          this.addressPerson.set(companies);
          this.isLoading.set(false);
        });
      }
    });
    //track when popup closed
    effect(()=>{
      this.staffMembersService.trackPopup();
      if(this.companyRoleForm()?.instance){
        this.resetData();
      }
    });
  }

  protected addRow():void{
    const validation=this.companyRoleForm().instance.validate();
    if(validation.isValid){
      this.addressPerson.set([...this.addressPerson(), this.company]);
      this.newCompanies.set([...this.newCompanies(),this.company]);
      this.company=new AddressPerson(null);
    }
  }
  protected updateName(e:ValueChangedEvent):void{
    let selectedCompany=this.companies().find(c => c.Id === e.value);
    this.company['Name']=selectedCompany?.Name ?? null;
  }
  protected validateCompanyIsNotAdded = (e:ValidationCallbackData):boolean=>{
    const selectedId = e.value;
    if (!selectedId) return true;
    return !this.addressPerson().some(p => p.ParentId === selectedId);
  }
  protected closePopup():void{
    this.popupClosed.emit();
    this.resetData();
  }
  protected onRowPrepared(e: RowPreparedEvent): void {
    if (e.rowType === 'data' && e.data?.Status === PersonStatus.ARCHIVED) {
      e.rowElement.style.backgroundColor = '#f5f5f5';
      e.rowElement.style.color = '#999';
    }
  }
  protected updateCompanies(e:RowUpdatingEvent):void{
    const updatedCompany = { ...e.oldData, ...e.newData };
    updatedCompany.Id 
     ? this.updatedCompanies.set([...this.updatedCompanies(),updatedCompany])
     : this.newCompanies.update(current => current.map(item => item.ParentId === updatedCompany.ParentId ? updatedCompany : item));
  }
  protected deleteCompanies(e:RowRemovingEvent):void{
    const deletedCompany = e.data;
    if(deletedCompany.Id){
      e.cancel = true;
      this.deletedCompanies.set([...this.deletedCompanies(),deletedCompany]);
      this.addressPerson.set(this.addressPerson().map((addressPerson)=>addressPerson.Id===deletedCompany.Id? {...addressPerson,Status:PersonStatus.ARCHIVED}:addressPerson));
    } else this.newCompanies.update(current => current.filter(item => !(item.ParentId === deletedCompany.ParentId)));
  }
  protected refreshColumns(value:boolean):void{
    this.isEditing.set(value);
  }
  protected persistAssociatedCompanies():void{
    this.isLoading.set(true);
    this.companyRoleService.persistAssociatedCompanies(this.selectedPersonId(),this.newCompanies(),this.updatedCompanies(),this.deletedCompanies()).subscribe({
      complete:()=> {
        this.isLoading.set(false);
        this.closePopup();
      },
      error:(error)=> {
        this.isLoading.set(false);
        error === CompanyRoleService.NO_PERSON_FOUND_ERROR 
        ? this.notificationService.callNotification({
          message: this.res("Ubt-CreateStaffMembers-Person-Not-Found"),
          type: ToastType.ERROR,
        })
        : this.notificationService.callNotification({
          message: NotificationMessage.Error,
          type: ToastType.ERROR,
        });
      }
    });
  }
  private restoreAddressPerson(e: ColumnButtonClickEvent):void{
    this.staffMembersService.archiveOrRestoreAddressPerson(e.row.data.Id,PersonStatus.ARCHIVED).pipe(first())
    .subscribe({
      complete:()=>{
        this.addressPerson.set(this.addressPerson().map((addressPerson)=>addressPerson.Id===e.row.data.Id? {...addressPerson,Status:PersonStatus.ACTIVE}:addressPerson));
      },
      error:()=>this.notificationService.callNotification({
        message: NotificationMessage.Error,
        type: ToastType.ERROR,
      })
    });
  }
  private resetData():void{
    this.newCompanies.set([]);
    this.updatedCompanies.set([]);
    this.deletedCompanies.set([]);
    this.addressPerson.set([]);
    this.staffMembersService.selectedPersonId.set('');
    this.company = new AddressPerson(null);
    this.companyRoleForm().instance.clear();
  }
}

